// Staff.js
// Fleet staff grid: pharmacy / role / staff name / contracted hours per month /
// date of last appraisal. Click the appraisal date to open a two-panel popup
// (list of appraisal dates on the left, selected appraisal contents on the right).
//
// Top controls: pharmacy selector, role selector, free-text search across all
// fields. Bottom totals: staff count + total contracted hours, both reactive to
// the active filter.
//
// Row colouring (appraisal status, annual cadence assumed):
//   - red   : last appraisal is in the past relative to its due date, or none on record
//   - amber : within one month of the next due appraisal
//   - none  : appraisal up to date
//
// NOTE: data is hard-coded placeholder for now — no API calls yet.

import React, { useState, useMemo } from "react";

/* ---------------- Placeholder dataset ----------------
   Each staff member has an appraisals array (most-recent-first). The grid shows
   the latest one; the popup lists them all. dueIntervalMonths drives the
   red/amber logic (annual = 12). */
const PLACEHOLDER_STAFF = [
  {
    id: 1, pharmacy: "Riverside Pharmacy", role: "Pharmacist",
    name: "Aisha Khan", contractedHours: 160, dueIntervalMonths: 12,
    appraisals: [
      { date: "2025-09-12", summary: "Strong clinical performance. Objectives met on NMS targets. Agreed to lead winter vaccination drive.", appraiser: "S. Patel" },
      { date: "2024-09-05", summary: "Solid year. Identified CPD gap in independent prescribing — enrolled on course.", appraiser: "S. Patel" },
      { date: "2023-08-30", summary: "First full-year appraisal. Settling in well, good rapport with team.", appraiser: "R. Owens" }
    ]
  },
  {
    id: 2, pharmacy: "Riverside Pharmacy", role: "Dispenser",
    name: "Tom Bradley", contractedHours: 150, dueIntervalMonths: 12,
    appraisals: [
      { date: "2024-11-20", summary: "Reliable and accurate. Discussed accuracy-checking qualification as next step.", appraiser: "Aisha Khan" }
    ]
  },
  {
    id: 3, pharmacy: "Riverside Pharmacy", role: "Counter Assistant",
    name: "Megan Lloyd", contractedHours: 80, dueIntervalMonths: 12,
    appraisals: []
  },
  {
    id: 4, pharmacy: "Highstreet Chemist", role: "Pharmacist",
    name: "David Owusu", contractedHours: 160, dueIntervalMonths: 12,
    appraisals: [
      { date: "2026-05-02", summary: "Excellent leadership during branch refit. Exceeded service-delivery objectives.", appraiser: "Head Office" },
      { date: "2025-04-28", summary: "Good year. Took on responsible-pharmacist mentoring duties.", appraiser: "Head Office" }
    ]
  },
  {
    id: 5, pharmacy: "Highstreet Chemist", role: "Technician",
    name: "Priya Sharma", contractedHours: 140, dueIntervalMonths: 12,
    appraisals: [
      { date: "2025-06-15", summary: "Confident running the dispensary. Agreed to take on stock-management lead.", appraiser: "David Owusu" }
    ]
  },
  {
    id: 6, pharmacy: "Highstreet Chemist", role: "Counter Assistant",
    name: "Jack Whitfield", contractedHours: 100, dueIntervalMonths: 12,
    appraisals: [
      { date: "2024-02-10", summary: "Good customer service. Needs to improve punctuality — to review in 6 months.", appraiser: "Priya Sharma" }
    ]
  },
  {
    id: 7, pharmacy: "Parkside Pharmacy", role: "Pharmacist",
    name: "Elena Rossi", contractedHours: 120, dueIntervalMonths: 12,
    appraisals: [
      { date: "2026-04-18", summary: "Part-time pharmacist, strong on clinical governance. Objectives all met.", appraiser: "Head Office" }
    ]
  },
  {
    id: 8, pharmacy: "Parkside Pharmacy", role: "Dispenser",
    name: "Mohammed Ali", contractedHours: 160, dueIntervalMonths: 12,
    appraisals: [
      { date: "2025-01-22", summary: "Dependable. Discussed cross-training on deliveries.", appraiser: "Elena Rossi" }
    ]
  },
  {
    id: 9, pharmacy: "Parkside Pharmacy", role: "Technician",
    name: "Sophie Turner", contractedHours: 150, dueIntervalMonths: 12,
    appraisals: []
  },
  {
    id: 10, pharmacy: "Canalside Pharmacy", role: "Pharmacist",
    name: "Raj Mehta", contractedHours: 160, dueIntervalMonths: 12,
    appraisals: [
      { date: "2025-12-01", summary: "New manager, settling in. Set objectives around team development and audit compliance.", appraiser: "Head Office" }
    ]
  },
  {
    id: 11, pharmacy: "Canalside Pharmacy", role: "Counter Assistant",
    name: "Grace Bennett", contractedHours: 90, dueIntervalMonths: 12,
    appraisals: [
      { date: "2026-05-20", summary: "Excellent on the medicines counter. Completed dispensing assistant course.", appraiser: "Raj Mehta" }
    ]
  }
];

/* ---------------- Date helpers ---------------- */
function parseISO(d) {
  if (!d) return null;
  const t = new Date(d + "T00:00:00");
  return isNaN(t.getTime()) ? null : t;
}
function fmtDate(d) {
  const t = parseISO(d);
  if (!t) return "—";
  return t.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function addMonths(date, months) {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
}

/* Appraisal status -> row background colour.
   - "overdue" (red)  : no appraisal on record, or next-due date has passed
   - "due-soon" (amber): within one month of next-due date
   - "ok" (none)      : up to date */
function appraisalStatus(staff) {
  const latest = staff.appraisals && staff.appraisals[0];
  if (!latest) return "overdue";
  const last = parseISO(latest.date);
  if (!last) return "overdue";
  const due = addMonths(last, staff.dueIntervalMonths || 12);
  const now = new Date();
  const oneMonthBefore = addMonths(due, -1);
  if (now >= due) return "overdue";
  if (now >= oneMonthBefore) return "due-soon";
  return "ok";
}
const STATUS_BG = { overdue: "#fee2e2", "due-soon": "#fef3c7", ok: "transparent" };

/* ---------------- Appraisal popup ---------------- */
function AppraisalPopup({ staff, onClose }) {
  const appraisals = staff.appraisals || [];
  const [selectedIdx, setSelectedIdx] = useState(appraisals.length ? 0 : -1);
  const selected = selectedIdx >= 0 ? appraisals[selectedIdx] : null;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
    }}>
      <div style={{
        background: "#fff", borderRadius: 8, width: "min(820px, 92vw)",
        maxHeight: "82vh", display: "flex", flexDirection: "column", overflow: "hidden",
        boxShadow: "0 10px 40px rgba(0,0,0,0.25)"
      }}>
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "14px 18px", borderBottom: "1px solid #e5e7eb"
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{staff.name}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{staff.role} · {staff.pharmacy}</div>
          </div>
          <button onClick={onClose} style={{
            border: "1px solid #d1d5db", background: "#fff", borderRadius: 4,
            width: 30, height: 30, cursor: "pointer", fontSize: 16, lineHeight: 1
          }}>×</button>
        </div>

        {/* Body: two panels */}
        <div style={{ display: "flex", minHeight: 0, flex: 1 }}>
          {/* Left: list of appraisal dates */}
          <div style={{
            width: 220, borderRight: "1px solid #e5e7eb", overflowY: "auto",
            background: "#f9fafb", flexShrink: 0
          }}>
            <div style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280" }}>
              Appraisals
            </div>
            {appraisals.length === 0 ? (
              <div style={{ padding: "10px 14px", fontSize: 13, color: "#dc2626" }}>None on record</div>
            ) : (
              appraisals.map((a, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedIdx(i)}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "10px 14px", border: "none",
                    borderBottom: "1px solid #eef0f3",
                    background: i === selectedIdx ? "#e0e7ff" : "transparent",
                    cursor: "pointer", fontSize: 13,
                    fontWeight: i === selectedIdx ? 600 : 400,
                    color: i === selectedIdx ? "#3730a3" : "#1f2937"
                  }}
                >
                  {fmtDate(a.date)}
                </button>
              ))
            )}
          </div>

          {/* Right: selected appraisal contents */}
          <div style={{ flex: 1, padding: 18, overflowY: "auto" }}>
            {selected ? (
              <>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>Date</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>{fmtDate(selected.date)}</div>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>Appraiser</div>
                <div style={{ fontSize: 14, marginBottom: 14 }}>{selected.appraiser || "—"}</div>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>Summary</div>
                <div style={{ fontSize: 14, lineHeight: 1.6, color: "#1f2937" }}>{selected.summary || "—"}</div>
              </>
            ) : (
              <div style={{ color: "#6b7280", fontSize: 14 }}>
                No appraisal on record for this staff member.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Main grid ---------------- */
export default function Staff() {
  const allStaff = PLACEHOLDER_STAFF;

  const [pharmacyFilter, setPharmacyFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [popupStaff, setPopupStaff] = useState(null);

  const pharmacies = useMemo(
    () => [...new Set(allStaff.map(s => s.pharmacy))].sort(),
    [allStaff]
  );
  const roles = useMemo(
    () => [...new Set(allStaff.map(s => s.role))].sort(),
    [allStaff]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allStaff.filter(s => {
      if (pharmacyFilter && s.pharmacy !== pharmacyFilter) return false;
      if (roleFilter && s.role !== roleFilter) return false;
      if (q) {
        const latest = s.appraisals && s.appraisals[0];
        const haystack = [
          s.pharmacy, s.role, s.name,
          String(s.contractedHours),
          latest ? fmtDate(latest.date) : ""
        ].join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [allStaff, pharmacyFilter, roleFilter, search]);

  const totalStaff = filtered.length;
  const totalHours = filtered.reduce((sum, s) => sum + (Number(s.contractedHours) || 0), 0);

  const th = {
    textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280",
    borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap"
  };
  const td = { padding: "10px 12px", fontSize: 13, borderBottom: "1px solid #f0f1f3", verticalAlign: "top" };
  const selStyle = {
    fontSize: 13, padding: "6px 10px", border: "1px solid #9ca3af",
    borderRadius: 4, background: "#fff", cursor: "pointer", minWidth: 180
  };

  return (
    <div style={{ padding: "16px 20px" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Staff</h2>
      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
        Fleet staff roster, contracted hours and appraisal status. Click an appraisal date for the full history.
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
        <label style={{ display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#6b7280" }}>
          PHARMACY
          <select value={pharmacyFilter} onChange={e => setPharmacyFilter(e.target.value)} style={selStyle}>
            <option value="">All pharmacies</option>
            {pharmacies.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label style={{ display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#6b7280" }}>
          ROLE
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={selStyle}>
            <option value="">All roles</option>
            {roles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label style={{ display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#6b7280", flex: 1, minWidth: 220 }}>
          SEARCH
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search across all fields…"
            style={{ fontSize: 13, padding: "6px 10px", border: "1px solid #9ca3af", borderRadius: 4 }}
          />
        </label>
      </div>

      {/* Grid */}
      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "#f9fafb" }}>
            <tr>
              <th style={th}>Pharmacy</th>
              <th style={th}>Role</th>
              <th style={th}>Staff name</th>
              <th style={{ ...th, textAlign: "right" }}>Contracted hrs/mth</th>
              <th style={th}>Last appraisal</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ ...td, textAlign: "center", color: "#6b7280", padding: 24 }}>No staff match the current filters.</td></tr>
            ) : (
              filtered.map(s => {
                const status = appraisalStatus(s);
                const latest = s.appraisals && s.appraisals[0];
                return (
                  <tr key={s.id} style={{ background: STATUS_BG[status] }}>
                    <td style={td}>{s.pharmacy}</td>
                    <td style={td}>{s.role}</td>
                    <td style={td}>{s.name}</td>
                    <td style={{ ...td, textAlign: "right" }}>{s.contractedHours}</td>
                    <td style={td}>
                      <span
                        onClick={() => setPopupStaff(s)}
                        style={{ color: "#4338ca", textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}
                        title="View appraisal history"
                      >
                        {latest ? fmtDate(latest.date) : "None on record"}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f3f4f6", fontWeight: 700 }}>
              <td style={{ ...td, borderBottom: "none" }} colSpan={2}>
                Total staff: {totalStaff}
              </td>
              <td style={{ ...td, borderBottom: "none" }} />
              <td style={{ ...td, borderBottom: "none", textAlign: "right" }}>{totalHours}</td>
              <td style={{ ...td, borderBottom: "none", color: "#6b7280", fontWeight: 400, fontSize: 12 }}>
                total contracted hrs/mth
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 18, marginTop: 12, fontSize: 12, color: "#374151", flexWrap: "wrap" }}>
        <span><span style={{ display: "inline-block", width: 12, height: 12, background: STATUS_BG.overdue, border: "1px solid #fca5a5", borderRadius: 2, verticalAlign: "middle", marginRight: 6 }} />Appraisal overdue / none on record</span>
        <span><span style={{ display: "inline-block", width: 12, height: 12, background: STATUS_BG["due-soon"], border: "1px solid #fcd34d", borderRadius: 2, verticalAlign: "middle", marginRight: 6 }} />Due within one month</span>
      </div>

      {popupStaff && <AppraisalPopup staff={popupStaff} onClose={() => setPopupStaff(null)} />}
    </div>
  );
}
